import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/authcontext';
import { getBidAnalytics, getClientAnalytics, getSubcontractorAnalytics, getProjectAnalytics } from '../services/api';
import { Bar, Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import styles from './analyticspage.module.css';
import { statusColors } from '../components/bidtable/bidtable';

// Register required Chart.js components
ChartJS.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale, 
  LinearScale, 
  BarElement,
  Title
);

function AnalyticsPage() {
  const { user } = useAuth();
  const [bidAnalytics, setBidAnalytics] = useState(null);
  const [clientAnalytics, setClientAnalytics] = useState(null);
  const [subcontractorAnalytics, setSubcontractorAnalytics] = useState(null);
  const [projectAnalytics, setProjectAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const hasLoadedOnce = useRef(false);

  // Global filter state
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [displayLimit, setDisplayLimit] = useState('top5'); // top5, top10, top20, all
  const [sortBy, setSortBy] = useState('totalAwarded'); // totalAwarded, totalBids, winRate

  // State for selected client, subcontractor, and client-specific projects
  const [selectedClient, setSelectedClient] = useState('all');
  const [selectedSubcontractor, setSelectedSubcontractor] = useState('all');
  const [selectedProjectsByClient, setSelectedProjectsByClient] = useState({}); // Track project selection per client

  const fetchAnalytics = useCallback(async () => {
    // Only show full loading screen on initial load
    if (!hasLoadedOnce.current) {
      setLoading(true);
    } else {
      setIsRefreshing(true);
    }
    setError(null);
    try {
      console.log('Analytics: Fetching analytics data for user:', user.uid);
      console.log('Analytics: Date range:', dateFrom, 'to', dateTo);

      const [bidData, clientData, subData, projectData] = await Promise.all([
        getBidAnalytics(user.uid, dateFrom || null, dateTo || null),
        getClientAnalytics(user.uid, dateFrom || null, dateTo || null),
        getSubcontractorAnalytics(user.uid, dateFrom || null, dateTo || null),
        getProjectAnalytics(user.uid, dateFrom || null, dateTo || null),
      ]);

      console.log('Analytics: Successfully fetched all analytics data');
      console.log('Client Analytics Data:', clientData);
      console.log('Project Analytics Data:', projectData);
      setBidAnalytics(bidData);
      setClientAnalytics(clientData);
      setSubcontractorAnalytics(subData);
      setProjectAnalytics(projectData);
      hasLoadedOnce.current = true;
    } catch (err) {
      console.error('Analytics: Failed to fetch analytics:', err);
      setError(err.message || 'Failed to load analytics. Please try again later.');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [user, dateFrom, dateTo]);

  useEffect(() => {
    if (user) {
      fetchAnalytics();
    }
  }, [user, fetchAnalytics]);

  // Format currency values for display
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Format percentage values for display
  const formatPercent = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'percent',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(value / 100);
  };

  // Create gradient for charts
  const createGradient = (ctx, color1, color2) => {
    const gradient = ctx.createLinearGradient(0, 0, 0, 400);
    gradient.addColorStop(0, color1);
    gradient.addColorStop(1, color2);
    return gradient;
  };

  // Modern color palette
  const colors = {
    primary: ['rgba(99, 102, 241, 0.8)', 'rgba(99, 102, 241, 1)'],
    secondary: ['rgba(168, 85, 247, 0.8)', 'rgba(168, 85, 247, 1)'],
    success: ['rgba(34, 197, 94, 0.8)', 'rgba(34, 197, 94, 1)'],
    warning: ['rgba(251, 146, 60, 0.8)', 'rgba(251, 146, 60, 1)'],
    danger: ['rgba(239, 68, 68, 0.8)', 'rgba(239, 68, 68, 1)'],
    info: ['rgba(59, 130, 246, 0.8)', 'rgba(59, 130, 246, 1)'],
    purple: ['rgba(147, 51, 234, 0.8)', 'rgba(147, 51, 234, 1)'],
    teal: ['rgba(20, 184, 166, 0.8)', 'rgba(20, 184, 166, 1)'],
  };

  // Modern chart configurations with enhanced styling
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#f3f4f6',
          font: {
            family: 'Poppins',
            size: 12,
            weight: '500',
          },
          padding: 15,
          usePointStyle: true,
          pointStyle: 'circle',
        },
      },
      tooltip: {
        backgroundColor: 'rgba(15, 23, 42, 0.95)',
        titleColor: '#f3f4f6',
        bodyColor: '#e0e7ff',
        borderColor: 'rgba(99, 102, 241, 0.5)',
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
        titleFont: {
          size: 13,
          weight: '600',
        },
        bodyFont: {
          size: 12,
        },
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              if (context.dataset.label?.includes('$')) {
                label += formatCurrency(context.parsed.y);
              } else if (context.dataset.label?.includes('%')) {
                label += formatPercent(context.parsed.y);
              } else {
                label += context.parsed.y;
              }
            }
            return label;
          }
        }
      }
    },
    animation: {
      duration: 1200,
      easing: 'easeInOutCubic',
    },
    scales: {
      x: {
        ticks: {
          color: '#9ca3af',
          font: {
            family: 'Poppins',
            size: 11,
            weight: '500',
          },
        },
        grid: {
          color: 'rgba(148, 163, 184, 0.1)',
          drawBorder: false,
        },
      },
      y: {
        ticks: {
          color: '#9ca3af',
          font: {
            family: 'Poppins',
            size: 11,
            weight: '500',
          },
        },
        grid: {
          color: 'rgba(148, 163, 184, 0.1)',
          drawBorder: false,
        },
      },
    },
  };

  // Modern mini chart options
  const miniChartOptions = {
    ...chartOptions,
    maintainAspectRatio: true,
    aspectRatio: 2.2,
    plugins: {
      ...chartOptions.plugins,
      legend: { display: false }
    },
    indexAxis: 'y',
    scales: {
      x: {
        beginAtZero: true,
        grid: {
          display: false
        },
        ticks: {
          color: '#9ca3af',
          font: {
            size: 10,
            weight: '500'
          }
        }
      },
      y: {
        grid: {
          display: false
        },
        ticks: {
          color: '#9ca3af',
          font: {
            size: 10,
            weight: '500'
          }
        }
      }
    }
  };

  // Get display limit number
  const getLimitNumber = () => {
    switch (displayLimit) {
      case 'top5': return 5;
      case 'top10': return 10;
      case 'top20': return 20;
      case 'all': return Infinity;
      default: return 5;
    }
  };

  // Sort function based on selected criteria
  const getSortFunction = () => {
    switch (sortBy) {
      case 'totalBids':
        return ([, a], [, b]) => (b.totalBids || 0) - (a.totalBids || 0);
      case 'winRate':
        return ([, a], [, b]) => (b.winRate || 0) - (a.winRate || 0);
      case 'totalAwarded':
      default:
        return ([, a], [, b]) => (b.totalAwarded || 0) - (a.totalAwarded || 0);
    }
  };

  // Get sorted and limited clients
  const getSortedClients = () => {
    return Object.entries(clientAnalytics || {})
      .sort(getSortFunction())
      .slice(0, getLimitNumber())
      .map(([name]) => name);
  };

  // Legacy: Get top 5 clients by total awarded amount (for backwards compatibility)
  const topClients = Object.entries(clientAnalytics || {})
    .sort(([, a], [, b]) => b.totalAwarded - a.totalAwarded)
    .slice(0, 5)
    .map(([name]) => name);

  // Get projects by client
  const getProjectsByClient = (clientName) => {
    if (!projectAnalytics) return [];
    return Object.entries(projectAnalytics)
      .filter(([, projectData]) => projectData.clientName === clientName)
      .map(([projectName]) => projectName);
  };

  // Get client data based on selection and global filters
  const getClientData = () => {
    if (!clientAnalytics) {
      console.log('No client analytics data');
      return {};
    }

    let result;
    if (selectedClient === 'all') {
      // Use global filters (sort and limit)
      const sortedClients = getSortedClients();
      result = sortedClients.reduce((acc, name) => {
        acc[name] = clientAnalytics[name];
        return acc;
      }, {});
    } else {
      // Show specific client
      result = { [selectedClient]: clientAnalytics[selectedClient] };
    }

    return result;
  };

  // Get project data for a specific client and project
  const getProjectDataForClient = (clientName, projectName) => {
    if (!projectAnalytics || !projectAnalytics[projectName]) return null;
    return projectAnalytics[projectName];
  };

  // Handle project selection for a specific client
  const handleProjectSelection = (clientName, projectName) => {
    setSelectedProjectsByClient(prev => ({
      ...prev,
      [clientName]: projectName
    }));
  };

  // Get sorted and limited subcontractors
  const getSortedSubcontractors = () => {
    if (!subcontractorAnalytics?.subcontractorAnalytics) return [];

    return Object.entries(subcontractorAnalytics.subcontractorAnalytics)
      .sort(([, a], [, b]) => {
        switch (sortBy) {
          case 'totalBids':
            return (b.totalBids || 0) - (a.totalBids || 0);
          case 'winRate':
            return (b.winRate || 0) - (a.winRate || 0);
          case 'totalAwarded':
          default:
            return (b.totalAwarded || 0) - (a.totalAwarded || 0);
        }
      })
      .slice(0, getLimitNumber())
      .map(([name]) => name);
  };

  // Get subcontractor data based on selection and global filters
  const getSubcontractorData = () => {
    if (!subcontractorAnalytics?.subcontractorAnalytics) return {};

    if (selectedSubcontractor === 'all') {
      // Use global filters (sort and limit)
      const sortedSubs = getSortedSubcontractors();
      return sortedSubs.reduce((acc, name) => {
        acc[name] = subcontractorAnalytics.subcontractorAnalytics[name];
        return acc;
      }, {});
    }
    return {
      [selectedSubcontractor]: subcontractorAnalytics.subcontractorAnalytics[selectedSubcontractor]
    };
  };

  if (loading && !hasLoadedOnce.current) {
    return <div className={styles.loadingContainer}>Loading analytics data...</div>;
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <h3>Error Loading Analytics</h3>
        <p>{error}</p>
        <button onClick={fetchAnalytics} className={styles.retryButton}>Retry</button>
      </div>
    );
  }

  // If no data is available
  if (!bidAnalytics || !clientAnalytics || !subcontractorAnalytics) {
    return (
      <div className={styles.emptyContainer}>
        <h3>No Analytics Data Available</h3>
        <p>Start by creating bids and sub-bids to generate analytics.</p>
      </div>
    );
  }

  // Modern pie chart options
  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'right',
        labels: {
          color: '#f3f4f6',
          font: {
            family: 'Poppins',
            size: 12,
            weight: '500',
          },
          padding: 15,
          usePointStyle: true,
          pointStyle: 'circle',
        },
      },
      tooltip: {
        backgroundColor: 'rgba(15, 23, 42, 0.95)',
        titleColor: '#f3f4f6',
        bodyColor: '#e0e7ff',
        borderColor: 'rgba(99, 102, 241, 0.5)',
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
        titleFont: {
          size: 13,
          weight: '600',
        },
        bodyFont: {
          size: 12,
        },
      },
    },
    animation: {
      duration: 1200,
      easing: 'easeInOutCubic',
      animateRotate: true,
      animateScale: true,
    },
  };

  // Bid type chart data - Modern design
  const bidTypeChartData = {
    labels: ['Project Bids', 'Standalone Bids'],
    datasets: [
      {
        label: 'Number of Bids',
        data: [bidAnalytics.projectBids, bidAnalytics.standaloneBids],
        backgroundColor: [colors.primary[0], colors.secondary[0]],
        borderColor: [colors.primary[1], colors.secondary[1]],
        borderWidth: 2,
        borderRadius: 8,
        hoverBackgroundColor: [colors.primary[1], colors.secondary[1]],
      },
    ],
  };

  // Win/loss chart data - Modern design
  const bidWinLossChartData = {
    labels: ['Wins', 'Losses'],
    datasets: [
      {
        label: 'Bid Outcomes',
        data: [bidAnalytics.wins, bidAnalytics.losses],
        backgroundColor: [colors.success[0], colors.danger[0]],
        borderColor: [colors.success[1], colors.danger[1]],
        borderWidth: 2,
        hoverBackgroundColor: [colors.success[1], colors.danger[1]],
        hoverBorderWidth: 3,
      },
    ],
  };

  // Bid amount chart data - Modern design
  const bidAmountChartData = {
    labels: ['Proposed', 'Awarded'],
    datasets: [
      {
        label: 'Amount ($)',
        data: [bidAnalytics.totalProposed, bidAnalytics.totalAwarded],
        backgroundColor: [colors.warning[0], colors.success[0]],
        borderColor: [colors.warning[1], colors.success[1]],
        borderWidth: 2,
        borderRadius: 8,
        hoverBackgroundColor: [colors.warning[1], colors.success[1]],
      },
    ],
  };

  // Client win rate chart data
  const clientWinRateChartData = {
    labels: Object.keys(clientAnalytics),
    datasets: [
      {
        label: 'Win Rate (%)',
        data: Object.values(clientAnalytics).map(client => client.winRate),
        backgroundColor: '#2196F3',
        borderColor: '#1976D2',
        borderWidth: 1,
      },
    ],
  };

  // Client profit chart data
  const clientProfitChartData = {
    labels: Object.keys(clientAnalytics),
    datasets: [
      {
        label: 'Profit ($)',
        data: Object.values(clientAnalytics).map(client => client.profit),
        backgroundColor: '#4CAF50',
        borderColor: '#388E3C',
        borderWidth: 1,
      },
    ],
  };

  // Modern status color mapping
  const modernStatusColors = {
    'Open': { bg: 'rgba(59, 130, 246, 0.8)', border: 'rgba(59, 130, 246, 1)' },
    'Submitted': { bg: 'rgba(251, 146, 60, 0.8)', border: 'rgba(251, 146, 60, 1)' },
    'Awarded': { bg: 'rgba(34, 197, 94, 0.8)', border: 'rgba(34, 197, 94, 1)' },
    'Lost': { bg: 'rgba(239, 68, 68, 0.8)', border: 'rgba(239, 68, 68, 1)' },
    'On Hold': { bg: 'rgba(156, 163, 175, 0.8)', border: 'rgba(156, 163, 175, 1)' }
  };

  // Bid Status Count chart data - Modern design
  const bidStatusCountChartData = {
    labels: Object.keys(bidAnalytics.statusCounts),
    datasets: [
      {
        label: 'Number of Bids',
        data: Object.values(bidAnalytics.statusCounts),
        backgroundColor: Object.keys(bidAnalytics.statusCounts).map(status => modernStatusColors[status]?.bg || 'rgba(107, 114, 128, 0.8)'),
        borderColor: Object.keys(bidAnalytics.statusCounts).map(status => modernStatusColors[status]?.border || 'rgba(107, 114, 128, 1)'),
        borderWidth: 2,
        borderRadius: 8,
        hoverBorderWidth: 3,
      },
    ],
  };

  // Bid Status Value chart data - Modern design
  const bidStatusValueChartData = {
    labels: Object.keys(bidAnalytics.statusValues),
    datasets: [
      {
        label: 'Value ($)',
        data: Object.values(bidAnalytics.statusValues),
        backgroundColor: Object.keys(bidAnalytics.statusValues).map(status => modernStatusColors[status]?.bg || 'rgba(107, 114, 128, 0.8)'),
        borderColor: Object.keys(bidAnalytics.statusValues).map(status => modernStatusColors[status]?.border || 'rgba(107, 114, 128, 1)'),
        borderWidth: 2,
        borderRadius: 8,
        hoverBorderWidth: 3,
      },
    ],
  };

  // Subcontractor win rate chart data
  const subWinRateChartData = {
    labels: Object.keys(subcontractorAnalytics.subcontractorAnalytics),
    datasets: [
      {
        label: 'Win Rate (%)',
        data: Object.values(subcontractorAnalytics.subcontractorAnalytics).map(sub => sub.winRate),
        backgroundColor: '#FF9800',
        borderColor: '#F57C00',
        borderWidth: 1,
      },
    ],
  };

  // Subcontractor awarded amount chart data
  const subAwardedChartData = {
    labels: Object.keys(subcontractorAnalytics.subcontractorAnalytics),
    datasets: [
      {
        label: 'Total Awarded ($)',
        data: Object.values(subcontractorAnalytics.subcontractorAnalytics).map(sub => sub.totalSubAwarded),
        backgroundColor: '#2196F3',
        borderColor: '#1976D2',
        borderWidth: 1,
      },
    ],
  };

  // Subcontractor projects chart data
  const subProjectsChartData = {
    labels: Object.keys(subcontractorAnalytics.subcontractorAnalytics),
    datasets: [
      {
        label: 'Number of Projects',
        data: Object.values(subcontractorAnalytics.subcontractorAnalytics).map(sub => sub.projectCount),
        backgroundColor: '#4CAF50',
        borderColor: '#388E3C',
        borderWidth: 1,
      },
    ],
  };
  
  // Create a stacked bar chart for client bid status counts - Modern design
  const clientStatusCountChartData = {
    labels: Object.keys(clientAnalytics),
    datasets: [
      {
        label: 'Open',
        data: Object.values(clientAnalytics).map(client => client.statusCounts?.Open || 0),
        backgroundColor: modernStatusColors.Open.bg,
        borderColor: modernStatusColors.Open.border,
        borderWidth: 2,
        borderRadius: 6,
      },
      {
        label: 'Submitted',
        data: Object.values(clientAnalytics).map(client => client.statusCounts?.Submitted || 0),
        backgroundColor: modernStatusColors.Submitted.bg,
        borderColor: modernStatusColors.Submitted.border,
        borderWidth: 2,
        borderRadius: 6,
      },
      {
        label: 'Awarded',
        data: Object.values(clientAnalytics).map(client => client.statusCounts?.Awarded || 0),
        backgroundColor: modernStatusColors.Awarded.bg,
        borderColor: modernStatusColors.Awarded.border,
        borderWidth: 2,
        borderRadius: 6,
      },
      {
        label: 'Lost',
        data: Object.values(clientAnalytics).map(client => client.statusCounts?.Lost || 0),
        backgroundColor: modernStatusColors.Lost.bg,
        borderColor: modernStatusColors.Lost.border,
        borderWidth: 2,
        borderRadius: 6,
      },
      {
        label: 'On Hold',
        data: Object.values(clientAnalytics).map(client => client.statusCounts?.['On Hold'] || 0),
        backgroundColor: modernStatusColors['On Hold'].bg,
        borderColor: modernStatusColors['On Hold'].border,
        borderWidth: 2,
        borderRadius: 6,
      },
    ],
  };
  
  // Remove the subcontractor status chart data since we don't need it anymore
  const subcontractorStatusChartData = null;

  return (
    <div className={styles.container}>
      <div className={styles.pageTitleRow}>
        <h1 className={styles.pageTitle}>Analytics Dashboard</h1>
        {isRefreshing && <span className={styles.refreshingIndicator}>Updating...</span>}
      </div>

      {/* Global Filters Bar */}
      <div className={styles.globalFiltersBar}>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Date Range</label>
          <div className={styles.dateRangeInputs}>
            <input
              type="date"
              className={styles.dateInput}
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              placeholder="From"
            />
            <span className={styles.dateSeparator}>to</span>
            <input
              type="date"
              className={styles.dateInput}
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              placeholder="To"
            />
          </div>
        </div>

        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Show</label>
          <select
            className={styles.filterSelect}
            value={displayLimit}
            onChange={(e) => setDisplayLimit(e.target.value)}
          >
            <option value="top5">Top 5</option>
            <option value="top10">Top 10</option>
            <option value="top20">Top 20</option>
            <option value="all">All</option>
          </select>
        </div>

        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Sort By</label>
          <select
            className={styles.filterSelect}
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="totalAwarded">Highest $ Value</option>
            <option value="totalBids">Most Jobs</option>
            <option value="winRate">Best Win Rate</option>
          </select>
        </div>

        {(dateFrom || dateTo) && (
          <button
            className={styles.clearFiltersButton}
            onClick={() => {
              setDateFrom('');
              setDateTo('');
            }}
          >
            Clear Dates
          </button>
        )}
      </div>

      {/* Summary Cards - CFO Metrics */}
      <div className={styles.summaryCards}>
        <div className={styles.card}>
          <h3>Pipeline Value</h3>
          <div className={styles.cardValue}>{formatCurrency(bidAnalytics.pipelineValue || 0)}</div>
          <div className={styles.cardSubtext}>Open + Submitted</div>
        </div>
        <div className={styles.card}>
          <h3>Win Rate</h3>
          <div className={styles.cardValue}>{formatPercent(bidAnalytics.winRate)}</div>
          <div className={styles.cardSubtext}>{bidAnalytics.wins} / {bidAnalytics.totalBids} bids</div>
        </div>
        <div className={styles.card}>
          <h3>Total Awarded</h3>
          <div className={styles.cardValue}>{formatCurrency(bidAnalytics.totalAwarded)}</div>
          <div className={styles.cardSubtext}>Booked Revenue</div>
        </div>
        <div className={styles.card}>
          <h3>Profit Margin</h3>
          <div className={styles.cardValue}>{formatPercent(bidAnalytics.profitMargin || 0)}</div>
          <div className={styles.cardSubtext}>Buyout Margin</div>
        </div>
        <div className={styles.card}>
          <h3>Buyout Profit</h3>
          <div className={styles.cardValue}>{formatCurrency(bidAnalytics.buyoutProfit || 0)}</div>
          <div className={styles.cardSubtext}>{formatCurrency(bidAnalytics.totalAwarded)} - {formatCurrency(bidAnalytics.totalSubBidAwarded || 0)}</div>
        </div>
        <div className={styles.card}>
          <h3>Total Proposed</h3>
          <div className={styles.cardValue}>{formatCurrency(bidAnalytics.totalProposed)}</div>
          <div className={styles.cardSubtext}>{bidAnalytics.totalBids} unique projects</div>
        </div>
      </div>
      
      {/* Bid Analytics Section */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Bid Analytics</h2>
        <div className={styles.chartsContainer}>
          <div className={styles.chartWrapper}>
            <h3>Bid Types</h3>
            <div className={styles.chartContainer}>
              <Bar 
                data={bidTypeChartData} 
                options={{
                  ...chartOptions,
                  plugins: {
                    ...chartOptions.plugins,
                    legend: { display: false }
                  }
                }} 
              />
            </div>
          </div>
          
          <div className={styles.chartWrapper}>
            <h3>Win/Loss</h3>
            <div className={styles.chartContainer}>
              <Pie 
                data={bidWinLossChartData} 
                options={pieChartOptions} 
              />
            </div>
          </div>
          
          <div className={styles.chartWrapper}>
            <h3>Proposed vs Awarded</h3>
            <div className={styles.chartContainer}>
              <Bar 
                data={bidAmountChartData} 
                options={{
                  ...chartOptions,
                  plugins: {
                    ...chartOptions.plugins,
                    legend: { display: false }
                  }
                }} 
              />
            </div>
          </div>
        </div>
        
        {/* New Status-based Analytics */}
        <h3 className={styles.subSectionTitle}>Status-based Analytics</h3>
        <div className={styles.chartsContainer}>
          <div className={styles.chartWrapper}>
            <h3>Bids by Status</h3>
            <div className={styles.chartContainer}>
              <Bar 
                data={bidStatusCountChartData} 
                options={{
                  ...chartOptions,
                  plugins: {
                    ...chartOptions.plugins,
                    legend: { display: false }
                  }
                }} 
              />
            </div>
          </div>
          
          <div className={styles.chartWrapper}>
            <h3>Bid Value by Status</h3>
            <div className={styles.chartContainer}>
              <Bar 
                data={bidStatusValueChartData} 
                options={{
                  ...chartOptions,
                  plugins: {
                    ...chartOptions.plugins,
                    legend: { display: false }
                  }
                }} 
              />
            </div>
          </div>
        </div>
      </section>
      
      {/* Client Analytics Section - Redesigned */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Client Performance</h2>
          <select
            className={styles.filterSelect}
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
          >
            <option value="all">
              {displayLimit === 'all' ? 'All Clients' : `Top ${getLimitNumber()} by ${sortBy === 'totalAwarded' ? '$ Value' : sortBy === 'totalBids' ? 'Jobs' : 'Win Rate'}`}
            </option>
            {Object.keys(clientAnalytics || {}).map(client => (
              <option key={client} value={client}>{client}</option>
            ))}
          </select>
        </div>

        <p className={styles.sectionDescription}>
          {selectedClient === 'all'
            ? `Showing ${displayLimit === 'all' ? 'all' : `top ${getLimitNumber()}`} clients sorted by ${sortBy === 'totalAwarded' ? 'highest $ value' : sortBy === 'totalBids' ? 'most jobs' : 'best win rate'}`
            : `Showing metrics for ${selectedClient}`}
        </p>

        {/* Modern Client Table */}
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Client</th>
                <th style={{ textAlign: 'center' }}>Bids</th>
                <th style={{ textAlign: 'center' }}>Win Rate</th>
                <th style={{ textAlign: 'right' }}>Total Proposed</th>
                <th style={{ textAlign: 'right' }}>Total Awarded</th>
                <th style={{ textAlign: 'right' }}>Buyout Profit</th>
                <th style={{ textAlign: 'center' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(getClientData()).map(([name, data]) => {
                if (!data) return null;

                // Calculate profit margin (only for project bids, not standalone)
                const hasSubBids = data.subTotalAwarded > 0;
                const profitMargin = hasSubBids && data.totalAwarded > 0
                  ? ((data.buyoutProfit / data.totalAwarded) * 100)
                  : 0;

                return (
                  <tr key={name} className={styles.clientRow}>
                    <td>
                      <div className={styles.clientNameCell}>
                        <div className={styles.clientName}>{name}</div>
                        <div className={styles.clientSubtext}>
                          {data.wins} wins • {data.losses} losses
                        </div>
                      </div>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <div className={styles.bidCount}>{data.totalBids}</div>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <div className={styles.winRateBadge} style={{
                        background: data.winRate >= 70 ? 'rgba(34, 197, 94, 0.15)' :
                                   data.winRate >= 40 ? 'rgba(251, 146, 60, 0.15)' :
                                   'rgba(239, 68, 68, 0.15)',
                        color: data.winRate >= 70 ? 'rgb(34, 197, 94)' :
                               data.winRate >= 40 ? 'rgb(251, 146, 60)' :
                               'rgb(239, 68, 68)',
                        border: `1px solid ${data.winRate >= 70 ? 'rgba(34, 197, 94, 0.3)' :
                                             data.winRate >= 40 ? 'rgba(251, 146, 60, 0.3)' :
                                             'rgba(239, 68, 68, 0.3)'}`
                      }}>
                        {formatPercent(data.winRate)}
                      </div>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div className={styles.currencyValue}>
                        {formatCurrency(data.totalProposed)}
                      </div>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div className={styles.currencyValue}>
                        {formatCurrency(data.totalAwarded)}
                      </div>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {hasSubBids ? (
                        <div className={styles.profitCell}>
                          <div className={styles.currencyValue}>
                            {formatCurrency(data.buyoutProfit)}
                          </div>
                          <div className={styles.profitMargin} style={{
                            color: profitMargin > 0 ? 'rgb(34, 197, 94)' : 'rgb(156, 163, 175)'
                          }}>
                            {formatPercent(profitMargin)} margin
                          </div>
                        </div>
                      ) : (
                        <div className={styles.naText}>
                          N/A
                        </div>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <div className={styles.statusBadges}>
                        {data.statusCounts && Object.entries(data.statusCounts).map(([status, count]) => {
                          if (count === 0) return null;
                          return (
                            <span
                              key={status}
                              className={styles.statusBadge}
                              style={{
                                background: modernStatusColors[status]?.bg || 'rgba(107, 114, 128, 0.15)',
                                color: '#ffffff',
                                border: `1px solid ${modernStatusColors[status]?.border || 'rgb(107, 114, 128)'}`
                              }}
                            >
                              {status}: {count}
                            </span>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
      
      {/* Subcontractor Analytics Section - Redesigned */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Subcontractor Performance</h2>
          <select
            className={styles.filterSelect}
            value={selectedSubcontractor}
            onChange={(e) => setSelectedSubcontractor(e.target.value)}
          >
            <option value="all">
              {displayLimit === 'all' ? 'All Subcontractors' : `Top ${getLimitNumber()} by ${sortBy === 'totalAwarded' ? '$ Value' : sortBy === 'totalBids' ? 'Jobs' : 'Win Rate'}`}
            </option>
            {Object.keys(subcontractorAnalytics?.subcontractorAnalytics || {}).map(sub => (
              <option key={sub} value={sub}>{sub}</option>
            ))}
          </select>
        </div>

        <p className={styles.sectionDescription}>
          {selectedSubcontractor === 'all'
            ? `Showing ${displayLimit === 'all' ? 'all' : `top ${getLimitNumber()}`} subcontractors sorted by ${sortBy === 'totalAwarded' ? 'highest $ value' : sortBy === 'totalBids' ? 'most jobs' : 'best win rate'}`
            : `Showing metrics for ${selectedSubcontractor}`}
        </p>

        {/* Modern Subcontractor Table */}
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Subcontractor</th>
                <th style={{ textAlign: 'center' }}>Sub-Bids</th>
                <th style={{ textAlign: 'center' }}>Win Rate</th>
                <th style={{ textAlign: 'right' }}>Total Bid</th>
                <th style={{ textAlign: 'right' }}>Total Awarded</th>
                <th style={{ textAlign: 'center' }}>Projects</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(getSubcontractorData()).map(([name, data]) => {
                if (!data) return null;

                return (
                  <tr key={name} className={styles.clientRow}>
                    <td>
                      <div className={styles.clientNameCell}>
                        <div className={styles.clientName}>{name}</div>
                        <div className={styles.clientSubtext}>
                          {data.wins || 0} wins • {data.losses || 0} losses
                        </div>
                      </div>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <div className={styles.bidCount}>{data.totalSubBids}</div>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <div className={styles.winRateBadge} style={{
                        background: data.winRate >= 70 ? 'rgba(34, 197, 94, 0.15)' :
                                   data.winRate >= 40 ? 'rgba(251, 146, 60, 0.15)' :
                                   'rgba(239, 68, 68, 0.15)',
                        color: data.winRate >= 70 ? 'rgb(34, 197, 94)' :
                               data.winRate >= 40 ? 'rgb(251, 146, 60)' :
                               'rgb(239, 68, 68)',
                        border: `1px solid ${data.winRate >= 70 ? 'rgba(34, 197, 94, 0.3)' :
                                             data.winRate >= 40 ? 'rgba(251, 146, 60, 0.3)' :
                                             'rgba(239, 68, 68, 0.3)'}`
                      }}>
                        {formatPercent(data.winRate)}
                      </div>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div className={styles.currencyValue}>
                        {formatCurrency(data.totalBid)}
                      </div>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div className={styles.currencyValue}>
                        {formatCurrency(data.totalSubAwarded)}
                      </div>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <div className={styles.bidCount}>
                        {data.projectCount > 0 ? data.projectCount : (data.totalSubBids > 0 ? data.totalSubBids : '-')}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
      
      {/* Subcontractor Leaderboard */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Subcontractor Leaderboard</h2>
        <p className={styles.sectionDescription}>Top Subcontractors by Total Awarded Amount</p>
        
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Rank</th>
                <th>Subcontractor</th>
                <th>Total Awarded</th>
                <th>Total Bids</th>
              </tr>
            </thead>
            <tbody>
              {subcontractorAnalytics.leaderboard.map((sub, index) => (
                <tr key={sub.name}>
                  <td>{index + 1}</td>
                  <td>{sub.name}</td>
                  <td>{formatCurrency(sub.totalSubAwarded)}</td>
                  <td>{sub.totalSubBids}</td>
                </tr>
              ))}
              {subcontractorAnalytics.leaderboard.length === 0 && (
                <tr>
                  <td colSpan="4" className={styles.emptyRow}>No subcontractor data available</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

export default AnalyticsPage; 